import { getProductInfo, viewItemList } from '@/utils/analytics.js';
import { useEffect, useState, useContext, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { WaveBreadCrumb, HomePageBanner, SortingAndFilters } from '@desquared/wave-components-library';
import cn from 'classnames';

import OffersCatalogue from '@/components/OffersCatalogue/OffersCatalogue';
import CategoryTitle from '@/components/CategoryTitle/CategoryTitle';
import FiltersFAB from '@/components/FiltersFAB/FiltersFAB';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import PageLayout from '@/components/PageLayout';
import OffersCategoriesMenu from '@/components/OffersCategoriesMenu/OffersCategoriesMenu';
import FiltersAside from '@/components/FiltersAside/FiltersAside';

import { getCmsHomepageLayout } from '@/api/cms.js';

import { SORT_OPTIONS } from '@/constants/sort';

import { wave } from '@/api/config';
import { getV31Products } from '@/api/Products';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useCategoryFilters, { OFFERS_OPTIONS } from '@/hooks/useCategoryFilters.js';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { getLayoutProps } from '@/utils/layout/layout';
import { getCategoriesFromFile } from '@/utils/getCategories';
import {
  createCategoriesForBundle,
  getCategoryMetadata,
  getCategoryPathArrayFromSlug,
  getProductsWithEmptyCategory,
} from '@/utils/category';
import { createOfferCategories } from '@/utils/trimmCategories';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import { checkListStatus } from '@/utils/lists';
import { setBanner } from '@/utils/homePageCmsCall';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import { transformOfferSlugsAndRemoveDuplicates, getOffersCategoryPathsFromFile } from '@/utils/staticPaths';
import { useTranslations } from 'next-intl';
import DefaultProductImage from '../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';
import { useVisibleCategories } from '@/contexts/VisibleCategoriesContext';

import styles from '@/styles/Categories.module.css';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';

export default function Offers({ responsiveLogo, allCategories, selectedCategory, emptyStates, imageBanners }) {
  const router = useRouter();
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const { bundleOffersSummary } = useBundleOffersSummary();
  const isMobile = useWindowUnderThreshold(850);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState(router.query.sort || 0);
  const { productTags } = useProductTags();
  const [limitations, setLimitations] = useState([]);
  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const { visibleCategories } = useVisibleCategories();
  // const [bundleCategories, setBundleCategories] = useState([]);
  const transformedImageBanners = useMemo(() => {
    return setBanner(imageBanners);
  }, [imageBanners]);

  const t = useTranslations();
  const a = useTranslations('common.asides');

  const offerCategorySubtitle = selectedCategory.description
    ? selectedCategory.description
    : t('category_details.subtitle', { categoryTitle: selectedCategory?.name });

  const { slug } = router.query;

  useEffect(() => {
    if (isFiltersSheetOpen) {
      getCategoryFilters();
    }
  }, [isFiltersSheetOpen]);

  const {
    getCategoryFilters,
    isLoading: isFiltersLoading,
    attributes,
    brands,
    categoryKinds,
    tags,
    selectedFilters,
    handleSelectFilter,
    numberOfSelectedFilters,
    clearFilters,
    handleApplyFilters,
    getFiltersFromUrl,
  } = useCategoryFilters(
    selectedCategory?._id,
    {
      allowAttributes: true,
      allowBrands: false,
      allowCategoryKinds: true,
      allowTags: true,
    },
    {
      offers: [
        {
          id: 'withBundleOffers',
          name: 'Συνδυαστικές',
        },
        {
          id: 'withOffer',
          name: 'Εκπτωτικές',
        },
      ],
      tags: [],
      brands: [],
      attributes: [],
      categoryKinds: [],
    }
  );

  //we need to trigger a router update to apply the initial filters
  useEffect(() => {
    if (router.asPath.indexOf(OFFERS_OPTIONS[0].id) === -1 && router.asPath.indexOf(OFFERS_OPTIONS[1].id) === -1) {
      handleSelectFilter('offers', [OFFERS_OPTIONS[0], OFFERS_OPTIONS[1]]);
      handleApplyFilters();
    }
  }, [selectedCategory?._id]);

  const bundleCategories = useMemo(() => {
    return createCategoriesForBundle(allCategories, selectedCategory?._id || '0');
  }, [allCategories, selectedCategory?._id]);

  // useEffect(() => {
  //   setIsFiltersSheetOpen(false);

  //   setBundleCategories(createCategoriesForBundle(allCategories, selectedCategory?._id || '0', visibleCategories));
  //   //eslint-disable-next-line
  // }, [router.asPath, visibleCategories]);

  const handleLoadProducts = async ({ pageParam }) => {
    const { filtersWithIds, offerFilters } = getFiltersFromUrl();

    const newProducts = await getV31Products(
      {
        pageNumber: pageParam.toString(),
        collectionTypes: getCollectionTypes(),
        sort: [SORT_OPTIONS[selectedSort]],
        //this will stop returning products grouped by category
        sortPriorityType: 'USER_DEFINED',
        category: selectedCategory?._id && selectedCategory?._id !== '0' ? selectedCategory?._id : '',
        pageSize: 30,
        ...filtersWithIds,
        ...offerFilters,
      },
      router.locale
    );

    return newProducts;
  };

  const {
    data: offersPaginatedResults,
    fetchNextPage,
    isLoading: isLoadingOfferResults,
    isFetchingNextPage,
  } = useInfiniteQuery({
    enabled: !!selectedCategory?._id,
    queryKey: ['offers/[...slug]', router.query, router.locale, selectedCategory, selectedSort],
    queryFn: handleLoadProducts,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
  });

  const products = useMemo(() => {
    if (!offersPaginatedResults) return [];

    return offersPaginatedResults.pages.flatMap(page => page).map(enhanceProductWithSlug);
  }, [offersPaginatedResults]);

  useEffect(() => {
    if (products?.length > 0) {
      viewItemList(
        products.map((i, idx) => getProductInfo(i, { index: idx + 1 }, router.query.slug[0])),
        {
          item_list_id: 'offers',
          item_list_name: selectedCategory.name,
        }
      );
    }
  }, [products?.length]);

  const handleLoadMore = fetchNextPage;

  useEffect(() => {
    async function getLimitations() {
      const limitation = await getOrFetchOrderLimitations();
      setLimitations(limitation);
    }
    getLimitations();
  }, []);

  //handles the user clicking on a category from the menu
  const onCategoriesClick = id => {
    const newCategory = getCategoryMetadata(id, router.locale);

    if (newCategory) {
      router.push(`/${newCategory?.fullSlug.replace('categories', 'offers')}?withOffer=true&withBundleOffers=true`);
    }
  };

  const breadCrumbItems = [{ title: t('offer_details.breadcrumb_title'), link: '/offers' }];

  const onEmptyClick = () => {
    router.replace(`/offers`);
  };

  if (bundleCategories.length === 0) return <div style={{ height: '100vh' }} />;
  return (
    <>
      <Head>
        <title>{process.env.displayName}</title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        {isMobile ? (
          <div className={styles.breadCrumbContainer}>
            <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} lastElementClickable />
          </div>
        ) : null}
        <div className={styles.categoriesWrapper}>
          <div className={styles.categoriesContainer}>
            <div className={styles.categoriesInnerContainer}>
              <OffersCategoriesMenu
                onCategoriesClick={onCategoriesClick}
                bundleCategories={bundleCategories}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
          <div className={styles.productsContainer}>
            {isMobile ? null : (
              <div className={styles.breadCrumbContainer}>
                <WaveBreadCrumb LinkComponent={Link} items={breadCrumbItems} iconLink={'/'} lastElementClickable />
              </div>
            )}

            {isMobile ? null : (
              <div className={styles.titleContainer}>
                <CategoryTitle title={selectedCategory?.name} subtitle={offerCategorySubtitle} />
                <div className={cn(styles.buttonWrapper)}>
                  <SortingAndFilters
                    text={t('category_details.sorting_filters.btn_title')}
                    numberOfFilters={numberOfSelectedFilters || undefined}
                    onClick={() => {
                      setIsFiltersSheetOpen(true);
                    }}
                  />
                </div>
              </div>
            )}
            {isMobile ? (
              <>
                <ScrollToTop />
                <FiltersFAB
                  onClick={() => {
                    setIsFiltersSheetOpen(true);
                  }}
                />
              </>
            ) : (
              <ScrollToTop />
            )}
            {slug[1] !== undefined && transformedImageBanners?.length > 0 && (
              <HomePageBanner
                isFixedWidth={true}
                images={transformedImageBanners}
                refreshTime={1000000}
                classOption={cn(styles.banner, styles.offersBanner)}
                LinkComponent={Link}
                showPagination={false}
              />
            )}

            <OffersCatalogue
              isLoading={isLoadingOfferResults || !selectedCategory?._id}
              isFetchingNextPage={isFetchingNextPage}
              handleLoadMore={handleLoadMore}
              limitations={limitations}
              checkStatus={checkListStatus}
              onEmptyClick={onEmptyClick}
              bundleOffersSummary={bundleOffersSummary}
              products={getProductsWithEmptyCategory(
                products,
                productTags,
                bundleOffersSummary,
                personalizedProductsIds,
                a
              )}
              defaultProductImage={defaultProdImg}
              selectedCategory={selectedCategory?.name}
            />
          </div>
        </div>
      </main>
      {isFiltersSheetOpen && (
        <FiltersAside
          isOffers
          isOpen={isFiltersSheetOpen}
          isLoading={isFiltersLoading}
          handleApplyFilters={handleApplyFilters}
          clearFilters={clearFilters}
          attributes={attributes}
          brands={brands}
          categoryKinds={categoryKinds}
          tags={tags}
          handleClose={() => setIsFiltersSheetOpen(false)}
          selectedFilters={selectedFilters}
          setSelectedFilters={handleSelectFilter}
          selectedSort={selectedSort}
          setSelectedSort={setSelectedSort}
          hasClosest={false}
          hideOffers={process.env.hideOffers}
        />
      )}
    </>
  );
}

Offers.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticPaths({ locales }) {
  const allCategoryPaths = getOffersCategoryPathsFromFile();
  const allOffersPaths = transformOfferSlugsAndRemoveDuplicates(allCategoryPaths);
  const paths = allOffersPaths
    .filter(i => i)
    .map(path => {
      const pathArr = path.split('/');
      pathArr.shift();
      return {
        params: { slug: pathArr },
      };
    });

  let localizedPaths = [];
  for (const locale of locales) {
    localizedPaths = [...localizedPaths, ...paths.map(path => ({ ...path, locale }))];
  }

  return { paths: localizedPaths, fallback: false };
}

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 1 } }, context.locale);
  const configuration = await getOrFetchConfiguration();
  const displayStrategies = configuration?.catalog?.offer?.categories?.displayStrategies;

  const categoriesFromFile = await getCategoriesFromFile(context.locale);
  const allCategories = createOfferCategories(categoriesFromFile, displayStrategies);
  const selectedCategoryInfo = getCategoryPathArrayFromSlug(categoriesFromFile, context.params.slug);
  const selectedCategoryTree = selectedCategoryInfo.categories;
  async function getV31ProductsWithAPIKey(resource) {
    const response = await wave.get('/v3.1/products', {
      headers: { 'x-api-key': process.env.PRIVATE_API_KEY, 'Accept-Language': context.locale },
      params: {
        available: true,
        pageNumber: 0,
        pageSize: 30,
        withAnyOffer: true,
        ...resource,
      },
    });

    return response.data;
  }
  let initialProducts = [];
  try {
    initialProducts = await getV31ProductsWithAPIKey({
      category: selectedCategoryTree[selectedCategoryTree.length - 1]._id,

      sort: 'position|asc',
      categorySearchType: 'ALL_LEVELS',
      pageSize: 30,
    });
  } catch (err) {
    console.error('>>>getStaticProps initialProducts error');
  }

  const mainCategory = layoutProps.categories.find(el => el.integratorId == -1 && el.friendlyId == -1);

  const { data: BannerData } = await getCmsHomepageLayout(mainCategory?.carousel_id);
  let imageBanners = [];
  if (BannerData.included?.homepage_banner_web_item?.length > 0 && mainCategory?.carousel_id) {
    imageBanners = BannerData.included.homepage_banner_web_item;
  }

  const messages = {
    common: (await import(`../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    category_details: (await import(`../../../prebuild-data/i18n/${context.locale}/category_details.json`)).default,
    offer_details: (await import(`../../../prebuild-data/i18n/${context.locale}/offer_details.json`)).default,
    offers: (await import(`../../../prebuild-data/i18n/${context.locale}/offers.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      selectedCategory: selectedCategoryTree[0],
      initialProducts: initialProducts.map(enhanceProductWithSlug),
      allCategories: allCategories.map(i => {
        return {
          ...i,
          icon: i?.icon || null,
          selectedIcon: i?.selectedIcon || null,
          top_banners: i?.top_banners || null,
          side_banners: i?.side_banners || null,
          isFeatured: i?.isFeatured || null,
          position: i?.position || null,
        };
      }),
      messages,
      imageBanners,
    },
  };
}
